/**
 * Readiness dial. Conic gauge showing composite preparedness score 0..100.
 * Click-to-flip shows breakdown per axis.
 */
import { withVault, injectCommonStyles } from './_shared.ts';

const TPL = /* html */ `
<style>
.dial-frame { display: grid; gap: 16px; }
.dial-wrap {
  display: grid;
  place-items: center;
  position: relative;
}
.dial {
  --val: 0;
  width: clamp(220px, 56vw, 280px);
  aspect-ratio: 1;
  border-radius: 50%;
  background:
    conic-gradient(from -90deg,
      var(--indigo) 0deg,
      var(--indigo-glow) calc(var(--val) * 3.6deg),
      oklch(60% 0.040 50 / 0.18) calc(var(--val) * 3.6deg),
      oklch(60% 0.040 50 / 0.18) 360deg
    );
  position: relative;
  cursor: pointer;
  filter: drop-shadow(0 1px 0 oklch(40% 0.040 50 / 0.10));
  transition: transform 220ms cubic-bezier(0.32, 0.72, 0, 1);
}
.dial:hover { transform: scale(1.02); }
.dial::after {
  content: '';
  position: absolute;
  inset: 18px;
  border-radius: 50%;
  background: var(--parchment-warm);
  box-shadow:
    inset 0 1px 0 oklch(100% 0.012 80 / 0.7),
    0 0 0 1px var(--rule-hair);
}
.dial-inner {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  text-align: center;
  z-index: 2;
}
.dial-score {
  font-family: var(--display);
  font-weight: 600;
  font-size: clamp(64px, 14vw, 88px);
  color: var(--indigo);
  line-height: 0.96;
  letter-spacing: -0.02em;
}
.dial-of {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.32em;
  color: var(--ink-faint);
  text-transform: uppercase;
}
.dial-label {
  text-align: center;
  font-family: var(--display);
  font-style: italic;
  color: var(--ink);
  font-size: 16px;
}
.next {
  text-align: center;
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: var(--indigo);
  padding: 6px 14px;
  border: 1px solid var(--rule-indigo);
  background: var(--indigo-wash);
  border-radius: 99px;
  justify-self: center;
}
.breakdown {
  display: none;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px 14px;
  margin-top: 8px;
}
.breakdown.open { display: grid; }
.b-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 6px;
  align-items: baseline;
  font-family: var(--display);
  font-size: 14px;
  padding: 6px 0;
  border-bottom: 1px dashed var(--rule-hair);
}
.b-row .v {
  font-family: var(--mono);
  font-size: 12px;
  color: var(--indigo);
}
.b-row .k { color: var(--ink-dim); text-transform: capitalize; }
</style>
<section class="frame dial-frame">
  <h2>Readiness <span class="deva">तैयारी</span></h2>
  <div class="kicker">Composite preparedness · click dial for breakdown</div>
  <div class="dial-wrap">
    <div class="dial" id="dial">
      <div class="dial-inner">
        <div class="dial-score" id="score">0</div>
        <div class="dial-of">of one hundred</div>
      </div>
    </div>
  </div>
  <div class="dial-label" id="status-label">Begin with one nominee</div>
  <div class="next" id="next-action">→</div>
  <div class="breakdown" id="breakdown"></div>
</section>
`;

export class WasiyatReadinessDial extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub: (() => void) | null = null;
  #open = false;

  connectedCallback(): void {
    injectCommonStyles(this.#shadow);
    this.#shadow.innerHTML += TPL;
    const dial = this.#shadow.getElementById('dial');
    const breakdown = this.#shadow.getElementById('breakdown');
    if (dial && breakdown) {
      dial.addEventListener('click', () => {
        this.#open = !this.#open;
        breakdown.classList.toggle('open', this.#open);
      });
    }
    this.#unsub = withVault(this, (v, root) => {
      const score = root.getElementById('score');
      const dialEl = root.getElementById('dial') as HTMLElement | null;
      const lbl = root.getElementById('status-label');
      const next = root.getElementById('next-action');
      const bd = root.getElementById('breakdown');
      if (score) score.textContent = String(v.readiness.score);
      if (dialEl) dialEl.style.setProperty('--val', String(v.readiness.score));
      if (lbl) lbl.textContent = this.#statusLabel(v.readiness.score);
      if (next) next.textContent = `Next · ${v.readiness.next_action}`;
      if (bd) {
        bd.innerHTML = Object.entries(v.readiness.breakdown)
          .map(([k, val]) => `<div class="b-row"><span class="k">${k}</span><span class="v">${val}</span></div>`)
          .join('');
      }
    });
  }

  disconnectedCallback(): void { this.#unsub?.(); }

  #statusLabel(score: number): string {
    if (score < 20) return 'Begin. Even one nominee changes everything.';
    if (score < 40) return 'A page is filled. Keep going.';
    if (score < 60) return 'The book takes shape.';
    if (score < 80) return 'Most affairs are in order.';
    if (score < 95) return 'Almost sealed. One last sweep.';
    return 'The vault is in good standing.';
  }
}

customElements.define('wasiyat-readiness-dial', WasiyatReadinessDial);
